import React, { useState } from 'react';
import { Camera, Instagram, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

/**
 * Modal shown right before payment redirect.
 * Asks the customer for explicit consent to photograph/film guests at the
 * event and post those images/videos on social media.
 *
 * The customer MUST tick the box to enable the "Continue to Payment" button.
 * They may also decline, in which case the booking proceeds but the admin
 * is flagged not to post any media from the event.
 */
export const MediaConsentModal = ({ open, onAccept, onDecline, loading }) => {
  const [agreed, setAgreed] = useState(false);

  if (!open) return null;

  const handleAccept = () => {
    if (!agreed) return;
    onAccept(true);
  };

  const handleDecline = () => {
    setAgreed(false);
    onDecline();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="media-consent-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full my-8 max-h-[90vh] overflow-y-auto">
        <div className="bg-[#8B7355] text-white px-6 py-5 flex items-center gap-3 rounded-t-lg">
          <Camera className="w-7 h-7 text-[#C9A961]" />
          <h2 className="font-serif text-2xl italic">One last thing</h2>
        </div>

        <div className="p-6 space-y-5 text-[#8B7355]">
          <p className="leading-relaxed">
            We love sharing the magic from our events on social media to help showcase
            the booth and inspire future clients. Before we head to payment, we just
            need your permission.
          </p>

          <div className="bg-[#F5F1E8] border-l-4 border-[#C9A961] p-4 rounded">
            <div className="flex gap-3">
              <Instagram className="w-5 h-5 text-[#C9A961] mt-0.5 flex-shrink-0" />
              <p className="text-sm leading-relaxed">
                I give Shot to the Nines permission to <strong>photograph and film
                guests using the booth</strong> at my event, and to <strong>post
                those photos and videos on social media</strong> (Instagram, Facebook,
                website, etc.) for promotional purposes.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded text-sm">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="leading-relaxed text-amber-900">
                If you&apos;d prefer we <strong>don&apos;t share any photos or videos</strong> from
                your event publicly, just leave the box below unticked. Your booking
                will proceed normally and we&apos;ll keep everything private.
              </p>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded border-2 border-[#8B7355]/20 hover:border-[#C9A961] transition-colors">
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => setAgreed(Boolean(v))}
              className="mt-1"
              data-testid="media-consent-checkbox"
            />
            <span className="text-sm leading-relaxed">
              Yes, I give Shot to the Nines permission to take photos/videos at my event
              and share them on social media.
            </span>
          </label>
        </div>

        <div className="bg-[#F5F1E8] px-6 py-4 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end rounded-b-lg border-t border-[#8B7355]/10">
          <Button
            type="button"
            variant="outline"
            onClick={handleDecline}
            disabled={loading}
            className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white rounded-none font-serif"
            data-testid="media-consent-decline-btn"
          >
            Continue without consent
          </Button>
          <Button
            type="button"
            onClick={handleAccept}
            disabled={!agreed || loading}
            className="bg-[#8B7355] hover:bg-[#6B5845] text-white rounded-none font-serif disabled:opacity-50"
            data-testid="media-consent-accept-btn"
          >
            Continue Enquiry
          </Button>
        </div>
      </div>
    </div>
  );
};
